// © 2013 - 2024 Tinder, Inc., ALL RIGHTS RESERVED

import {
  FONT_FAMILY_PROXIMA_NOVA,
  GenericObsidianTheme,
  ObsidianTheme,
  Theme,
  themeCssVariable,
} from '@tinderapp/ui-core';

// Allows arbitrary token names while autocompleting of Obsidian token names

type ThemeValueNamespace = keyof Omit<Theme, 'name' | 'themeId' | 'version' | 'dark'>;

const VALUE_DEFAULTS = {
  dark: false,
  color: 'inherit',
  gradient: 'none',
  gradientFill: 'none',
  fontFamily: FONT_FAMILY_PROXIMA_NOVA,
  fontSize: '1rem',
  fontWeight: 400,
  lineHeight: 1,
  letterSpacing: 0,
  letterCasing: 'none',
  sizing: 0,
};

export type ExtractThemeNamespaceMap<
  N extends ThemeValueNamespace,
  T extends Theme = GenericObsidianTheme
> = {
  [key in keyof T[N]]: string;
};

export function createThemeCssVariableAccessor<
  N extends ThemeValueNamespace,
  T extends Theme = GenericObsidianTheme
>(namespace: N): ExtractThemeNamespaceMap<N, T>;
export function createThemeCssVariableAccessor<
  N extends ThemeValueNamespace,
  T extends Theme = GenericObsidianTheme
>(namespace: 'gradientFill'): ExtractThemeNamespaceMap<N, T>;
export function createThemeCssVariableAccessor<
  N extends ThemeValueNamespace,
  T extends Theme = GenericObsidianTheme
>(namespace: ThemeValueNamespace | 'gradientFill'): ExtractThemeNamespaceMap<N, T> {
  return new Proxy({} as ExtractThemeNamespaceMap<N, T>, {
    get(_target, key: string) {
      return themeCssVariable(namespace, key, `${VALUE_DEFAULTS[namespace]}`);
    },
  });
}

/**
 * Theme CSS Variables
 * ===================
 *
 * Using CSS variables allows component styles to be defined statically, which helps performance.
 */

export interface ThemeCssVarGetter<T extends Theme = GenericObsidianTheme> {
  dark: string;
  color: ExtractThemeNamespaceMap<'color', T>;
  gradient: ExtractThemeNamespaceMap<'gradient', T>;
  gradientFill: ExtractThemeNamespaceMap<'gradient', T>;
  fontFamily: ExtractThemeNamespaceMap<'fontFamily', T>;
  fontSize: ExtractThemeNamespaceMap<'fontSize', T>;
  fontWeight: ExtractThemeNamespaceMap<'fontWeight', T>;
  lineHeight: ExtractThemeNamespaceMap<'lineHeight', T>;
  letterSpacing: ExtractThemeNamespaceMap<'letterSpacing', T>;
  letterCasing: ExtractThemeNamespaceMap<'letterCasing', T>;
  sizing: ExtractThemeNamespaceMap<'sizing', T>;
}

export const themeCssVar: ThemeCssVarGetter<ObsidianTheme> = {
  dark: themeCssVariable('dark', undefined, `${+VALUE_DEFAULTS.dark}`),
  color: createThemeCssVariableAccessor('color'),
  gradient: createThemeCssVariableAccessor('gradient'),
  gradientFill: createThemeCssVariableAccessor<'gradient'>('gradientFill'),
  fontFamily: createThemeCssVariableAccessor('fontFamily'),
  fontSize: createThemeCssVariableAccessor('fontSize'),
  fontWeight: createThemeCssVariableAccessor('fontWeight'),
  lineHeight: createThemeCssVariableAccessor('lineHeight'),
  letterSpacing: createThemeCssVariableAccessor('letterSpacing'),
  letterCasing: createThemeCssVariableAccessor('letterCasing'),
  sizing: createThemeCssVariableAccessor('sizing'),
};

/**
 * The default behavior for `themeCssVar` triggers type errors when non-Obsidian keys are used.
 *
 * When using a Volcano/sandbox/custom theme, you will want to bypass those errors by casting
 * `themeCssVar` to `unknown` then to `ThemeCssVarGetter<YourThemeType>` to specify a custom type
 * for the theme.
 *
 * `customThemeCssVar` demonstrates how to do that, or you can use it directly to allow any string
 * as a key while still getting autocompleting on design tokens that are part of Obsidian.
 */
export const customThemeCssVar = themeCssVar as unknown as ThemeCssVarGetter<GenericObsidianTheme>;
